import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Sparkles,
  Trophy,
  Award,
  Users,
  Heart,
  MessageCircle,
  Target,
  Star,
  CheckCircle,
  ArrowRight,
  Globe
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';

function About() {
  const { language } = useLanguage();

  const sections = language === 'zh' ? [
    {
      icon: Sparkles,
      title: '什么是 K2 Kudos？',
      content: `K2 Kudos 是 Kids2 的员工认可平台。它让您能够认可同事在工作中展现的出色表现，
        特别是当他们体现了我们的核心价值观和 30 项基本准则时。`
    },
    {
      icon: Heart,
      title: '如何发送表彰',
      content: null,
      steps: [
        '点击"发送表彰"按钮（在动态页面顶部）',
        '搜索并选择您想要表彰的同事',
        '选择一个核心价值观（C-协作、D-敬业、E-创业精神、F-乐趣）',
        '选择一项基本准则（#1-#30）',
        '撰写一条有意义的消息（至少50个中文字符）',
        '点击提交！'
      ]
    },
    {
      icon: Trophy,
      title: '查看您的表彰',
      content: `点击导航栏中的"我的表彰"可以查看：`,
      bullets: [
        '您收到的所有表彰',
        '您发出的所有表彰',
        '您获得的徽章',
        '获得更多徽章的进度'
      ]
    },
    {
      icon: Award,
      title: '徽章系统',
      content: `当您收到3次或更多带有相同标签的表彰时，您将获得徽章：`,
      badges: [
        { type: 'value', label: 'K2 价值观徽章', desc: '收到3次相同核心价值观的表彰（显示为彩色圆圈：C=蓝色、D=绿色、E=琥珀色、F=粉色）' },
        { type: 'fundamental', label: '基本准则专注徽章', desc: '收到3次相同基本准则的表彰（显示为带编号的盾牌）' },
        { type: 'ace', label: 'ACE 2026 徽章', desc: '在三个ACE类别中各获得一个基本准则徽章：Activate（激活）、Compete（竞争）、Elevate（提升）' }
      ]
    },
    {
      icon: Users,
      title: '经理功能',
      content: `如果您是经理，您会在导航栏中看到一个"团队"标签。在这里您可以查看：`,
      bullets: [
        '您直属下属收到的表彰',
        '您直属下属发出的表彰',
        '团队参与度指标'
      ]
    },
    {
      icon: Globe,
      title: '语言',
      content: `K2 Kudos 支持英语和中文。点击标题中的语言按钮（"中文"或"EN"）即可切换。
        表彰消息会自动翻译成另一种语言。`
    },
    {
      icon: Star,
      title: '使用技巧',
      content: null,
      bullets: [
        '撰写具体的消息——解释他们做了什么以及为什么重要',
        '使用点赞和评论来参与他人的表彰',
        '定期查看您的徽章进度以追踪成就',
        '使用"热门"标签查看最受关注的表彰',
        '动态中的表彰按最新排序'
      ]
    }
  ] : [
    {
      icon: Sparkles,
      title: 'What is K2 Kudos?',
      content: `K2 Kudos is Kids2's employee recognition platform. It allows you to recognize
        colleagues for great work, especially when they demonstrate our core values and
        30 fundamentals.`
    },
    {
      icon: Heart,
      title: 'How to Give Recognition',
      content: null,
      steps: [
        'Click the "Give Recognition" button (found at the top of the Feed page)',
        'Search for and select the colleague you want to recognize',
        'Choose a Core Value (C-Collaborative, D-Dedicated, E-Entrepreneurial, F-Fun)',
        'Choose a Fundamental (#1-#30)',
        'Write a meaningful message (minimum 140 characters for English)',
        'Click Submit!'
      ]
    },
    {
      icon: Trophy,
      title: 'Viewing Your Kudos',
      content: `Click "My Kudos" in the navigation bar to see:`,
      bullets: [
        'All recognitions you\'ve received',
        'All recognitions you\'ve given',
        'Badges you\'ve earned',
        'Your progress toward earning more badges'
      ]
    },
    {
      icon: Award,
      title: 'Badge System',
      content: `You earn badges when you receive 3 or more recognitions with the same tag:`,
      badges: [
        { type: 'value', label: 'K2 Value Badge', desc: 'Receive 3 recognitions with the same core value (shown as colored circles: C=blue, D=green, E=amber, F=pink)' },
        { type: 'fundamental', label: 'Fundamentally Focused Badge', desc: 'Receive 3 recognitions with the same fundamental (shown as numbered shields)' },
        { type: 'ace', label: 'ACE 2026 Badge', desc: 'Earn a fundamental badge from each of the three ACE categories: Activate, Compete, Elevate' }
      ]
    },
    {
      icon: Users,
      title: 'Manager Features',
      content: `If you're a manager, you'll see a "Team" tab in the navigation. Here you can view:`,
      bullets: [
        'Recognitions received by your direct reports',
        'Recognitions given by your direct reports',
        'Team engagement metrics'
      ]
    },
    {
      icon: Globe,
      title: 'Language',
      content: `K2 Kudos supports both English and Chinese. Click the language button in the
        header ("中文" or "EN") to switch. Recognition messages are automatically translated
        to the other language.`
    },
    {
      icon: Star,
      title: 'Tips for Great Recognition',
      content: null,
      bullets: [
        'Be specific in your message — explain what they did and why it mattered',
        'Use likes and comments to engage with others\' recognitions',
        'Check your badge progress regularly to track achievements',
        'Use the "Popular" tab to see trending recognitions',
        'Recognitions appear in the feed sorted by most recent'
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          {language === 'zh' ? '关于 K2 Kudos' : 'About K2 Kudos'}
        </h1>
        <p className="text-muted-foreground">
          {language === 'zh'
            ? '了解如何使用员工认可平台'
            : 'Learn how to use the employee recognition platform'}
        </p>
      </div>

      {/* Quick Start */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
              <CheckCircle className="h-5 w-5 text-primary-foreground" />
            </div>
            <h2 className="text-xl font-semibold">
              {language === 'zh' ? '快速入门' : 'Quick Start'}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="px-3 py-1 rounded-full bg-primary/20 text-primary font-medium">
              1. {language === 'zh' ? '发送表彰' : 'Give Recognition'}
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className="px-3 py-1 rounded-full bg-primary/20 text-primary font-medium">
              2. {language === 'zh' ? '收集徽章' : 'Collect Badges'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      {sections.map((section, idx) => (
        <Card key={idx}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-3 text-lg">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                <section.icon className="h-5 w-5 text-primary" />
              </div>
              {section.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            {section.content && (
              <p className="text-muted-foreground mb-3">{section.content}</p>
            )}

            {section.steps && (
              <ol className="space-y-2 ml-4">
                {section.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-foreground">{step}</span>
                  </li>
                ))}
              </ol>
            )}

            {section.bullets && (
              <ul className="space-y-1 ml-4">
                {section.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-primary mt-1.5">•</span>
                    <span className="text-foreground">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.badges && (
              <div className="space-y-3 mt-2">
                {section.badges.map((badge, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                    <div className="flex-shrink-0">
                      {badge.type === 'value' && (
                        <svg width="28" height="28" viewBox="0 0 44 44">
                          {/* Outer ring */}
                          <circle cx="22" cy="22" r="20" fill="#3b82f6" stroke="#3b82f6" strokeWidth="2" />
                          {/* Notches around edge */}
                          {[...Array(16)].map((_, i) => {
                            const angle = (i * 22.5) * Math.PI / 180;
                            const x1 = 22 + Math.cos(angle) * 17;
                            const y1 = 22 + Math.sin(angle) * 17;
                            const x2 = 22 + Math.cos(angle) * 20;
                            const y2 = 22 + Math.sin(angle) * 20;
                            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth="2" />;
                          })}
                          {/* Inner circle */}
                          <circle cx="22" cy="22" r="13" fill="#3b82f6" stroke="white" strokeWidth="1.5" />
                          {/* Letter */}
                          <text x="22" y="27" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">C</text>
                        </svg>
                      )}
                      {badge.type === 'fundamental' && (
                        <svg width="24" height="28" viewBox="0 0 28 32">
                          <path
                            d="M14 0 C6 0, 0 2, 0 6 L0 18 C0 24, 14 32, 14 32 C14 32, 28 24, 28 18 L28 6 C28 2, 22 0, 14 0 Z"
                            fill="#f59e0b"
                          />
                          <text x="14" y="18" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
                            1
                          </text>
                        </svg>
                      )}
                      {badge.type === 'ace' && (
                        <svg width="20" height="28" viewBox="0 0 24 32">
                          <rect x="0" y="0" width="24" height="32" rx="3" fill="white" stroke="#1a1a1a" strokeWidth="1.5"/>
                          <text x="12" y="13" textAnchor="middle" fill="#dc2626" fontSize="10" fontWeight="bold">A</text>
                          <text x="12" y="25" textAnchor="middle" fill="#dc2626" fontSize="12">♠</text>
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{badge.label}</p>
                      <p className="text-sm text-muted-foreground">{badge.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {section.note && (
              <p className="text-sm text-muted-foreground mt-3 italic">
                {section.note}
              </p>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground py-4">
        {language === 'zh'
          ? '有问题？请联系 IT 部门获取帮助。'
          : 'Have questions? Contact IT for assistance.'}
      </div>
    </div>
  );
}

export default About;
